import { useEffect, useRef, useState } from "react";

// How often an already-open tab re-checks for a newer deploy. Also re-checks
// immediately whenever the tab regains focus/visibility, so switching back to
// a long-idle tab catches up right away instead of waiting for the interval.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

async function fetchVersion() {
  try {
    // Cache-busted and cache: "no-store" belt-and-suspenders — /version.json
    // isn't hashed like /static/* so a CDN or browser cache holding onto a
    // stale copy would defeat the whole check.
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return data.version || null;
  } catch {
    return null;
  }
}

/*
  Tells a tab that's been open across a deploy to reload.

  There's no baked-in "my own version" constant to compare against — instead
  the first successful fetch after mount becomes the baseline, and every
  later poll compares against that. A deploy changes public/version.json
  (see scripts/generate-version.js), so once this tab's baseline no longer
  matches what's live, a newer build has shipped since this tab was loaded.
*/
export default function VersionWatcher() {
  const [outdated, setOutdated] = useState(false);
  const baselineRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const current = await fetchVersion();
      if (cancelled || !current) return;
      if (baselineRef.current === null) {
        baselineRef.current = current;
        return;
      }
      if (current !== baselineRef.current) {
        setOutdated(true);
      }
    };

    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!outdated) return null;

  return (
    <div
      className="fixed inset-0 z-[999] bg-white flex items-center justify-center p-6"
      data-testid="version-outdated-overlay"
    >
      <div className="max-w-sm w-full text-center border border-amber-300 bg-amber-50 rounded-sm px-6 py-6 shadow-lg">
        <p className="trk-uppercase-label text-amber-700 mb-2">Update available</p>
        <p className="text-sm text-zinc-700 mb-5">
          You're viewing an older version of Initiative Board. Please reload to get the latest updates.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-sm bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-5 py-2 transition-colors"
          data-testid="version-outdated-reload"
        >
          Reload now
        </button>
      </div>
    </div>
  );
}
